import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';

import { environment } from '../../../../environments/environment';
import { QueryParamsModel, QueryResultsModel } from '../../_base/crud';
import { currentUser } from '../../auth';
import { AppState } from '../../reducers';
import { Note } from '../_models/note.model';

export enum ViewContext {
  contacts = 'CONTACT',
  patients = 'PATIENT',
  doctors = 'DOCTOR',
  facility = 'FACILITY',
  pharmacies = 'PHARMACY',
  pharmacyGroups = 'PHARMACY_GROUP',
  drugGroups = 'DRUG_GROUP',
  drugs = 'DRUG',
  clients = 'CLIENT',
  insurances = 'INSURANCE',
}

@Injectable({ providedIn: 'root' })
export class NoteService {
  private companyId;
  private baseUrl = environment.CORE_BASE;

  constructor(private http: HttpClient, private store: Store<AppState>) {
    this.store.select(currentUser).subscribe((user) => (this.companyId = user.companyID));
  }

  private static getNotePageQueryString(queryParams: QueryParamsModel): string {
    const { pageNumber, pageSize, sortField, sortOrder, filter } = queryParams;
    let fromString = `page=${pageNumber}&size=${pageSize}&sort=pinned,desc`;

    if (sortField && sortOrder) fromString += `&sort=${sortField},${sortOrder}`;
    if (filter.mainId) fromString += `&main_id=${filter.mainId}`;
    if (filter.mainType) fromString += `&main_type=${filter.mainType}`;

    if (filter.searchTerm) {
      fromString += `&search_term=${filter.searchTerm}`;
    } else {
      if (filter.pinned) fromString += `&pinned=${filter.pinned}`;
      if (filter.entityId) fromString += `&entity_id=${filter.entityId}`;
      if (filter.contactId) fromString += `&contact_id=${filter.contactId}`;
      if (filter.subject) fromString += `&subject_has=${filter.subject}`;
      if (filter.description) fromString += `&description_has=${filter.description}`;
      if (filter.created)
        fromString += `&created=${filter.created}&user_timezone=${
          Intl.DateTimeFormat().resolvedOptions().timeZone
        }`;
      if (filter.updated)
        fromString += `&updated=${filter.updated}&user_timezone=${
          Intl.DateTimeFormat().resolvedOptions().timeZone
        }`;
      if (filter.createdBy) fromString += `&created_by=${filter.createdBy}`;
      if (filter.updatedBy) fromString += `&updated_by=${filter.updatedBy}`;
    }

    return fromString;
  }

  getNotePage(queryParams: QueryParamsModel): Observable<QueryResultsModel> {
    const params = new HttpParams({ fromString: NoteService.getNotePageQueryString(queryParams) });

    return this.http.get<QueryResultsModel>(`${this.baseUrl}/companies/${this.companyId}/notes`, {
      params,
    });
  }

  getNotesAsCSV(queryParams: QueryParamsModel): Observable<string> {
    const params = new HttpParams({ fromString: NoteService.getNotePageQueryString(queryParams) });

    return this.http.get(`${this.baseUrl}/companies/${this.companyId}/notes/csv`, {
      params,
      responseType: 'text',
    });
  }

  getNoteById(noteId: number): Observable<{ data: Note }> {
    return this.http.get<{ data: Note }>(
      `${this.baseUrl}/companies/${this.companyId}/notes/${noteId}`,
    );
  }

  createNote(note: Note): Observable<HttpResponse<{ data: Note }>> {
    return this.http.post<{ data: Note }>(
      `${this.baseUrl}/companies/${this.companyId}/notes`,
      note,
      { observe: 'response' },
    );
  }

  updateNote(note: Note): Observable<HttpResponse<{ data: Note }>> {
    return this.http.put<{ data: Note }>(
      `${this.baseUrl}/companies/${this.companyId}/notes/${note.id}`,
      note,
      { observe: 'response' },
    );
  }

  deleteNote(noteId: number): Observable<HttpResponse<void>> {
    return this.http.delete<void>(`${this.baseUrl}/companies/${this.companyId}/notes/${noteId}`, {
      observe: 'response',
    });
  }

  pinUnpinNote(note: Note): Observable<HttpResponse<{ data: Note }>> {
    return this.http.put<{ data: Note }>(
      `${this.baseUrl}/companies/${this.companyId}/notes/${note.id}/pin_unpin`,
      { pinned: !note.pinned },
      { observe: 'response' },
    );
  }
}
